import React from 'react';
import type { SubscriptionStatus } from '../lib/tenant';
import { ResponsiveCardValue } from '../components/common/ResponsiveCardValue';

/** Paying workspace with an activated subscription (excludes incomplete checkout). */
export function isActiveSubscriber(status: SubscriptionStatus): boolean {
  return status === 'active';
}

export function isPendingCheckout(status: SubscriptionStatus): boolean {
  return status === 'pending_payment';
}

/** Active or lapsed billing — excludes incomplete checkout profiles. */
export function isAccessManagedTenant(status: SubscriptionStatus): boolean {
  return status !== 'pending_payment';
}

export function billingLabel(s: SubscriptionStatus): string {
  if (s === 'active') return 'Active';
  if (s === 'pending_payment') return 'Pending payment';
  return 'Past due';
}

export function billingPillClass(s: SubscriptionStatus): string {
  if (s === 'active') return 'bg-emerald-500/15 text-emerald-800 ring-emerald-500/25';
  if (s === 'pending_payment') return 'bg-amber-500/15 text-amber-900 ring-amber-500/25';
  return 'bg-rose-500/15 text-rose-900 ring-rose-500/25';
}

/** Type DELETE to confirm irreversible super-admin removals. */
export function confirmPermanentDelete(label: string, entityName: string): boolean {
  const msg = `Permanently delete ${label} "${entityName}"?\n\nThis removes it from the database and cannot be undone.\n\nType DELETE to confirm.`;
  const typed = window.prompt(msg);
  return typed === 'DELETE';
}

export function SectionHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h2 className="text-xl font-semibold tracking-tight text-zinc-900">{title}</h2>
        {subtitle ? <p className="mt-1 max-w-3xl text-sm text-zinc-600">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function StatCard({
  label,
  value,
  hint,
  icon,
  tone = 'default',
}: {
  label: string;
  value: string | number;
  hint?: string;
  icon?: React.ReactNode;
  tone?: 'default' | 'success' | 'warning' | 'danger' | 'accent';
}) {
  const toneClass =
    tone === 'success'
      ? 'border-emerald-200/80 bg-emerald-50/50'
      : tone === 'warning'
        ? 'border-amber-200/80 bg-amber-50/50'
        : tone === 'danger'
          ? 'border-rose-200/80 bg-rose-50/50'
          : tone === 'accent'
            ? 'border-violet-200/80 bg-violet-50/50'
            : 'border-zinc-200 bg-white';
  return (
    <div className={`rounded-xl border p-4 shadow-sm ${toneClass}`}>
      <div className="flex items-center justify-between text-zinc-500">
        <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
        {icon}
      </div>
      <ResponsiveCardValue value={String(value)} className="mt-2 text-zinc-900" scale="hero" />
      {hint ? <p className="mt-1 text-xs text-zinc-500">{hint}</p> : null}
    </div>
  );
}

export function MetricGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{children}</div>;
}
